import React, { useMemo, useState } from 'react';
import { RTTS_SUPPORT_ISSUE_CATEGORIES, type LscSupportIssueCategory } from '../../lsc/types';
import { supportIssueCategoryLabel } from '../../lsc/supportIssueCopy';
import { STUDIO_COPY } from '../config';
import { createRttsSupportIssue } from '../support/rttsSupportApi';

export type ReportProblemPrefill = {
  jobId?: string;
  tier?: string;
  reelState?: string;
  errorCode?: string;
  funding?: string;
};

export function ReportProblemModal({
  open,
  prefill,
  onClose,
}: {
  open: boolean;
  prefill?: ReportProblemPrefill | null;
  onClose: () => void;
}) {
  const defaultCategory: LscSupportIssueCategory =
    prefill?.reelState === 'failed' || prefill?.errorCode === 'no_highlights'
      ? 'reel_failed'
      : 'other';
  const [category, setCategory] = useState<LscSupportIssueCategory>(defaultCategory);
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const context = useMemo(
    () => ({
      job_id: prefill?.jobId,
      tier: prefill?.tier,
      reel_state: prefill?.reelState,
      error_code: prefill?.errorCode,
      funding: prefill?.funding,
      surface: 'studio',
    }),
    [prefill]
  );

  if (!open) return null;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const text = description.trim();
    if (!text) {
      setError('Tell us what happened.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await createRttsSupportIssue({
        category,
        title: text.slice(0, 200),
        description: text,
        context,
      });
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not submit report');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 p-4" data-testid="studio-report-problem-modal">
      <div className="studio-panel w-full max-w-lg space-y-4">
        {done ? (
          <div className="space-y-3" data-testid="studio-report-problem-success">
            <h2 className="text-xl font-semibold">Report received</h2>
            <p>{STUDIO_COPY.reportProblemSuccess}</p>
            <button type="button" className="studio-btn-primary" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={(e) => void submit(e)} className="space-y-3">
            <h2 className="text-xl font-semibold">{STUDIO_COPY.reportProblem}</h2>
            <label className="block text-sm space-y-1">
              <span>Category</span>
              <select
                className="w-full rounded bg-black/40 p-2"
                value={category}
                onChange={(e) => setCategory(e.target.value as LscSupportIssueCategory)}
                data-testid="studio-report-problem-category"
              >
                {RTTS_SUPPORT_ISSUE_CATEGORIES.map((value) => (
                  <option key={value} value={value}>
                    {supportIssueCategoryLabel(value)}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm space-y-1">
              <span>What happened</span>
              <textarea
                className="w-full min-h-[120px] rounded bg-black/40 p-2"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                data-testid="studio-report-problem-description"
              />
            </label>
            {error ? (
              <p className="text-red-300 text-sm" role="alert">
                {error}
              </p>
            ) : null}
            <div className="flex gap-2">
              <button type="submit" className="studio-btn-primary" disabled={busy} data-testid="studio-report-problem-submit">
                {STUDIO_COPY.reportProblemSubmit}
              </button>
              <button type="button" className="studio-btn-ghost" onClick={onClose}>
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export function ReportProblemButton({
  label,
  prefill,
  testId,
}: {
  label: string;
  prefill?: ReportProblemPrefill;
  testId?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="studio-btn-ghost"
        onClick={() => setOpen(true)}
        data-testid={testId || 'studio-report-problem'}
      >
        {label}
      </button>
      <ReportProblemModal open={open} prefill={prefill} onClose={() => setOpen(false)} />
    </>
  );
}
